import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '../../lib/clientErrors.js';

type State = { failed: boolean };

/** Qualquer erro de uma tela vira um estado desenhado, nunca uma página em branco. */
export class ErrorBoundary extends Component<{ children: ReactNode; where?: string }, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[secretario] tela falhou', error, info.componentStack);
    reportClientError(this.props.where ?? 'tela', error);
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="pg" role="alert">
        <h1>Algo saiu do lugar</h1>
        <p className="pg-sub">Essa tela travou por aqui. Vamos tentar de novo?</p>
        <div className="pg-actions">
          <button className="pg-chip solid" onClick={() => window.location.reload()}>
            Recarregar
          </button>
          <a className="pg-chip" href="/">
            Ir para a tela inicial
          </a>
        </div>
      </div>
    );
  }
}

/**
 * Proteção das camadas que ficam por cima das páginas (conversa ao vivo,
 * painéis do palco, pedido rápido). Se uma delas falhar, só ela some e avisa
 * quem a abriu (`onFail`) para fechar; o resto do app continua de pé. Sem isso,
 * um erro numa camada derrubava a tela inteira, em branco, até reabrir o app.
 */
export class LayerBoundary extends Component<
  {
    children: ReactNode;
    where: string;
    onFail?: () => void;
    resetKey?: unknown;
    /** Quantas vezes por minuto a camada se refaz sozinha antes de desistir (`onFail`). */
    retries?: number;
  },
  State
> {
  override state: State = { failed: false };
  private failures: number[] = [];
  private retryTimer: ReturnType<typeof setTimeout> | undefined;

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[secretario] camada falhou (${this.props.where})`, error, info.componentStack);
    reportClientError(this.props.where, error);
    const now = Date.now();
    this.failures = [...this.failures.filter((at) => now - at < 60_000), now];
    if (this.failures.length <= (this.props.retries ?? 0)) {
      // Um erro de desenho não pode encerrar o que segue por baixo (a conversa ao vivo):
      // a camada some por um instante e volta inteira.
      this.retryTimer = setTimeout(() => this.setState({ failed: false }), 60);
      return;
    }
    this.props.onFail?.();
  }

  override componentWillUnmount() {
    clearTimeout(this.retryTimer);
  }

  override componentDidUpdate(previous: { resetKey?: unknown }) {
    // Mudou o que estava aberto (outra rota, outro painel): a camada volta a valer.
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}
